import { Button as MuiButton, Grid, styled, SxProps, Theme, ToggleButton, Tooltip, tooltipClasses, TooltipProps, Typography, useMediaQuery } from "@mui/material";
import { fixtureTime } from "../common/DateTimeHelper";
import { Button, CashIcon, CircleIcon, HalfCircleIcon } from "../reusable-components";
import React, { FormEvent, useEffect, useRef, useState } from 'react';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import { UnibetStatsIcon } from '../images/unibeticons';
import { formatFraction } from "../common/RoundHelper";
import SlimFixtureDetail from "./slimfixturedetail";
import { Fixture } from "../models/fixture";
import { DetailedRound } from "../models/round";

import { couponTotal, getDrawText, getTeamName, getWholeAndHalfPutsFromArray, safeLocalStorageGet, safeLocalStorageRemove, safeLocalStorageSet } from "./couponhelpers";
import { GetProPickLabel, IsLeo, IsOrangeDark, IsOrangeLight, IsRightToLeftTheme, IsVideo, themeC } from "../theme/themehelper";
import StakeMultiplier from "./stakemultiplier";
import { PoolXMoney } from "../models/money";
import ConfirmPayment, { ErrorResponse } from "../roundview/components/couponform/components/confirmpayment";
import { operatorAdviceLogin, operatorAdviceScrollToTop } from "../App";
import { track, trackBeginCheckout, trackCouponTool } from "../analytics/analytics";
import { usePlayer } from "../player/playerContext";
import { RoundInformation } from "../roundview/components/roundinformation";
import { useTranslation } from "react-i18next";
import { PoolXNumberFormat } from "../common/CurrencyFormatter";
import ReducedSystemSelector from "../roundview/components/reducedsystemselector";
import LoginDialog from "./logindialog";
import { FreeBet } from "../models/freebet";
import { ProPickSelector } from "../roundview/components/propickselector";
import { Bet, Coupon, CouponFixtureSummary, Leg, System } from "../models/couponshares";

function SlimCoupon({
    round,
    onSubmit,
    theme,
    showProPick,
    setShowProPick,
    showConfirm,
    setShowConfirm,
    submitFailed,
    onErrorMsgClose,
    submitError,
    submitting,
    stakeMultiplier,
    setStakeMultiplier,
    onChangeFixture,
    showRoundInfo,
    setShowRoundInfo,
    showRSystem,
    setShowRSystem,
    proPickRows,
    setProPickRows,
    coupon,
    couponFixtureSummaries,
    freebet,
    onClear,
    chosenRSystem,
    setChosenRSystem
}:
    {
        round: DetailedRound,
        onSubmit: (coupon: Coupon) => Promise<boolean>,
        theme: string,
        showProPick: boolean,
        setShowProPick: (show: boolean) => void,
        showConfirm: boolean,
        setShowConfirm: (show: boolean) => void,
        submitFailed: boolean,
        onErrorMsgClose: () => void,
        submitError: ErrorResponse | undefined,
        submitting: boolean,
        stakeMultiplier: number,
        setStakeMultiplier: (multiplier: number) => void,
        onChangeFixture: (index: number) => void,
        showRoundInfo: boolean,
        setShowRoundInfo: (show: boolean) => void,
        showRSystem: boolean,
        setShowRSystem: (show: boolean) => void,
        proPickRows: string[][] | undefined,
        setProPickRows: (rows: string[][] | undefined) => void,
        coupon: Coupon | undefined,
        couponFixtureSummaries: CouponFixtureSummary[] | undefined,
        freebet: FreeBet | undefined,
        onClear: () => void,
        chosenRSystem: System,
        setChosenRSystem: (system: System) => void
    }) {

    let emptyForm: Coupon | undefined;
    let emptyExpandedFixtures: number[] = [];
    let [formData, setFormData] = useState(emptyForm);
    let [expandedFixtures, setExpandedFixtures] = useState(emptyExpandedFixtures);
    let [counter, setCounter] = useState(0);
    let [valid, setValid] = useState(false);
    let [coversValid, setCoversValid] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    let playerResp: any = usePlayer();
    const { t } = useTranslation();
    const [input, setInput] = useState(emptyForm);
    const [open, setOpen] = useState({ index: -1, sign: "" });
    const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
    const typoRefs = useRef<{ [key: string]: HTMLSpanElement | null }>({});
    let [showLoginDialog, setShowLoginDialog] = useState(false);
    const isHockey = round.name.indexOf('Hockey') > -1;

    useEffect(() => {

        if (formData !== undefined && round !== undefined && proPickRows !== undefined && proPickRows.length === round.fixtures.length) {
            formData.bets[0].legs = round.fixtures.map((_fixture, index) => {
                let myLeg: Leg = { signs: proPickRows[index] };
                return myLeg;
            });
            setFormData(formData);
            setProPickRows(undefined);
            setValid(validateCoupon(formData));
        }

        if (formData !== undefined && round !== undefined && coupon !== undefined && coupon !== null) {

            formData.bets = coupon.bets;
            setFormData(formData);
            setValid(validateCoupon(formData));
        }

        if (formData !== undefined && round !== undefined && coupon === undefined && coupon !== null
            && (formData.bets?.[0]?.legs ?? []).map(leg => (leg?.signs ?? []).length).reduce((total, length) => total + length, 0) === 0
            && selectedIndex === 0
        ) {
            //First render

            const savedInput = safeLocalStorageGet("userInput");
            if (savedInput) {
                try {
                    const parsedJson = JSON.parse(savedInput);
                    const parsedCoupon = parsedJson as Coupon;
                    if (round.id === parsedCoupon.roundId) {
                        setFormData(parsedCoupon);
                        setValid(validateCoupon(parsedCoupon));
                    }
                    setInput(undefined);
                    safeLocalStorageRemove("userInput");

                } catch (error) {
                    safeLocalStorageRemove("userInput");
                }
            }
        }

    }, [formData, round, proPickRows, setProPickRows, coupon, selectedIndex])


    useEffect(() => {
        if (input && input.bets.length <= 1) {
            safeLocalStorageSet("userInput", JSON.stringify(input));
        }

    }, [input]);

    // Close tooltip when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            let clickedInside = false;

            const clickedElement = event.target as HTMLElement;

            Object.values(buttonRefs.current).forEach((button) => {
                if (button && button.innerText === clickedElement.innerText) {
                    clickedInside = true;
                }
            });
            Object.values(typoRefs.current).forEach((button) => {
                if (button && button.innerText === clickedElement.innerText) {
                    clickedInside = true;
                }
            });

            if (!clickedInside) {
                setOpen({ index: -1, sign: "" });
            }
        };
        if (open.index > -1) {
            document.addEventListener("click", handleClickOutside);
        }

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [open]);

    const isFirstRSystemRun = useRef(true);
    useEffect(() => {

        // Skip on initial mount so we don't reset formData (and overwrite any
        // restored/saved signs) before the saved coupon has been loaded.
        if (isFirstRSystemRun.current) {
            isFirstRSystemRun.current = false;
            return;
        }

        handleRSystemChange(chosenRSystem)

    }, [chosenRSystem]);

    useEffect(() => {
        if (round !== undefined) {
            setFormData(prev => prev !== undefined ? {
                ...prev,
                roundId: round.id
            } : prev);
        }
    }, [round]);

    let player = playerResp[0];
    let isRealMode = player && player._embedded && player._embedded.mode === 'real';

    const isMobileScreen = useMediaQuery('(max-width: 950px)');
    let isRightToLeft = IsRightToLeftTheme(theme);

    function newBet(fixtures: Fixture[]): Bet {

        let system: System = {
            type: "MATHEMATICAL",
        };

        const legs = fixtures.map((_fixture, _index) => {
            let myLeg: Leg = { signs: [] };
            return myLeg;
        });
        return (
            {
                system: system,
                legs: legs
            }
        );
    }

    const handleFixtureClick = (index: number) => {
        onChangeFixture(index);
        setSelectedIndex(index);
    }

    const handleTooltipToggle = (index: number, sign: string) => {
        setOpen({ index: index, sign: sign });
    };

    function updateProPickRows(rows: Array<Array<string>>): void {

        let bet = newBet(round.fixtures);
        bet.legs = round.fixtures.map((_fixture, index) => {
            let myLeg: Leg = { signs: rows[index] };
            return myLeg;
        });

        let coupon: Coupon | undefined = {
            bets: [bet],
            stakePerRow: round.operatorStakePerRow,
            roundId: round.id
        }

        setFormData(coupon);
        setValid(validateCoupon(coupon));
    }


    if (round !== undefined && formData === undefined) {

        let coupon: Coupon | undefined = {
            bets: [newBet(round.fixtures)],
            stakePerRow: round.operatorStakePerRow,
            roundId: round.id
        }
        setFormData(coupon);
    }

    if (round === undefined || formData === undefined) {
        return <></>
    }

    const isAfterDeadline = new Date(round.openTo) < new Date(Date.now());

    let locale = t('locale');
    const nf = PoolXNumberFormat(locale, {
        style: 'currency',
        currency: round.operatorStakePerRow.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    const nfOdds = PoolXNumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    const handleChange = (index: number, pick: string) => {

        if (isAfterDeadline || coupon !== undefined) {
            return;
        }

        // Defensive: ensure bets/legs/signs exist before accessing
        let newBets = formData?.bets ?? [newBet(round.fixtures)];
        if (!newBets[0]) newBets[0] = newBet(round.fixtures);
        if (!newBets[0].legs) newBets[0].legs = round.fixtures.map((_fixture) => ({ signs: [] } as any));
        let existing = newBets[0].legs[index]?.signs ?? [];

        let indexToRemove = existing.indexOf(pick);

        if (indexToRemove === -1 && newBets[0].legs.every(leg => (leg?.signs ?? []).length === 0)) {
            track("coupon_started", { round_id: round.id, product: round.name });
        }

        if (indexToRemove > -1) {
            existing.splice(indexToRemove, 1);
        } else {
            existing.push(pick);
        }

        newBets[0].legs[index] = { signs: existing } as any;

        let newFormData: Coupon = {
            bets: newBets,
            stakePerRow: formData?.stakePerRow ?? round.operatorStakePerRow,
            roundId: formData?.roundId ?? round.id,
            ...(formData && (formData as any).freebetId !== undefined ? { freebetId: (formData as any).freebetId } : {} as any)
        } as Coupon;

        setFormData(newFormData);
        setInput(newFormData);
        setValid(validateCoupon(newFormData));
    };

    const handleRSystemChange = (system: System) => {

        // Defensive: ensure a bet exists, otherwise create default
        let betObj = formData?.bets?.[0] ?? newBet(round.fixtures);
        betObj.system = system;

        let newFormData: Coupon = {
            bets: [betObj],
            stakePerRow: formData?.stakePerRow ?? round.operatorStakePerRow,
            roundId: formData?.roundId ?? round.id,
            ...(formData && (formData as any).freebetId !== undefined ? { freebetId: (formData as any).freebetId } : {} as any)
        } as Coupon;

        setFormData(newFormData);
        setValid(validateCoupon(newFormData));
    };

    const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
        <Tooltip {...props} classes={{ popper: className }} />
    ))(({ theme }) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: 'var(--background_1)',
            color: 'rgba(0, 0, 0, 0.87)',
            maxWidth: 300,
            fontSize: theme.typography.pxToRem(12),
            border: '1px solid #dadde9',
        },
    }));

    const TooltipContent = (textDetail: SxProps<Theme>) => <React.Fragment>
        <Grid container direction="row" justifyContent="space-between">
            <Grid item sx={{ margin: "3px" }}>
                <Grid container
                    sx={{ alignItems: "center" }}>
                    {CircleIcon}
                    <Typography
                        sx={textDetail}>{t('full_cover')}: {wholePuts} {reducedWhole} </Typography>
                </Grid>
            </Grid>
            <Grid item sx={{ margin: "3px" }}>
                <Grid container
                    sx={{ alignItems: "center" }}>
                    {HalfCircleIcon}
                    <Typography
                        sx={textDetail}>{t('half_cover')}: {halfPuts} {reducedHalf} </Typography>
                </Grid>
            </Grid>
        </Grid>
    </React.Fragment>

    function validateCoupon(coupon: Coupon | undefined): boolean {

        if (!coupon || !coupon.bets || coupon.bets.length === 0) {
            return false;
        }

        setInput(coupon);

        const { bets } = coupon;
        // Defensive: ensure there is at least one bet
        const firstBet = bets && bets.length > 0 ? bets[0] : undefined;
        if (!firstBet) return false;
        const { legs = [], system } = firstBet as any;

        let halfCover = 0;
        let fullCover = 0;

        let valid = true;
        for (let i = 0; i < legs.length; i++) {
            const signs = legs[i].signs;

            if (signs.length === 0) {
                valid = false;
                break;
            }

            if (signs.length === 2) {
                halfCover++;
            }

            if (signs.length === 3) {
                fullCover++;
            }
        }

        if (system.type === 'REDUCED' && system.reducedSystemId) {
            let parts = system.reducedSystemId.split('_');
            if (parts[1] !== fullCover.toString()) {
                valid = false;
            }
            if (parts[2] !== halfCover.toString()) {
                valid = false;
            }
        }

        if (freebet !== undefined) {

            if (freebet.numFull !== fullCover) {
                valid = false;
                setCoversValid(false);
            }
            if (freebet.numHalf !== halfCover) {
                valid = false;
                setCoversValid(false);
            }

            if (freebet.numFull === fullCover && freebet.numHalf === halfCover) {
                setCoversValid(true);
            }
        }

        return valid;
    }

    const handleExpand = (index: number) => {

        let existing = expandedFixtures;
        let indexToRemove = existing.indexOf(index);
        if (indexToRemove > -1) {
            existing.splice(indexToRemove, 1);
        } else {
            existing.push(index);
        }
        setExpandedFixtures(existing);
        setCounter((c) => c + 1)
    };

    const resetForm = () => {
        let bet: Coupon | undefined = {
            bets: [newBet(round.fixtures)],
            stakePerRow: round.operatorStakePerRow,
            roundId: round.id
        }
        setFormData(bet)
        setValid(validateCoupon(bet));
        setInput(undefined);
        safeLocalStorageRemove("userInput");
        onErrorMsgClose();
        onClear();
    }

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        onSubmit(formData!).then(result => {
            if (result) {
                resetForm();
            }
        }
        )
    }

    const handleError = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
    }

    function isSelected(index: number, sign: string): boolean {
        if ((formData?.bets?.length ?? 0) > 1)
            return false;
        if (formData?.bets?.[0]?.system?.type === "REDUCED" && couponFixtureSummaries)
            return false;
        return !!formData?.bets?.[0]?.legs?.[index]?.signs?.includes(sign);
    }

    function getSignStyle(round: DetailedRound, index: number, sign: string): SxProps<Theme> {

        if (couponFixtureSummaries && couponFixtureSummaries.length > 0 &&
            (formData!.bets.length > 1 || formData!.bets[0].system.type === "REDUCED")) {

            let summary = 0;

            switch (sign) {
                case "ONE":
                    summary = (couponFixtureSummaries[index]?.signs?.ONE?.rowsFraction ?? 0);
                    break;
                case "X":
                    summary = (couponFixtureSummaries[index]?.signs?.X?.rowsFraction ?? 0);
                    break;
                case "TWO":
                    summary = (couponFixtureSummaries[index]?.signs?.TWO?.rowsFraction ?? 0)
                    break;
            }

            let partial: SxProps<Theme> = {
                backgroundImage: 'linear-gradient(to top, var(--sign-background) 0 ' + summary * 100 + '%, var(--sign-background-not-selected) ' + summary * 100 + '% 100%)',
                color: summary > 0.5 ? 'var(--sign-color-selected)' : 'var(--sign-color)'
            }

            if (isAfterDeadline && round.preliminaryCorrectRow && round.preliminaryCorrectRow[index] === sign)
                return { ...button, ...partial, ...correct } as SxProps<Theme>

            return { ...button, ...partial } as SxProps<Theme>
        }


        if (isAfterDeadline && round.preliminaryCorrectRow && round.preliminaryCorrectRow[index] === sign)
            return { ...button, ...correct } as SxProps<Theme>

        return button;
    }

    let rowPrice = Number(round.operatorStakePerRow.amount);
    if (round.operatorFeePerRow !== undefined && round.operatorFeePerRow?.amount > 0) {
        rowPrice = rowPrice + Number(round.operatorFeePerRow.amount);
    }

    let placeBetColor = "var(--button-color)";
    let clearBetsBorder = "none";

    let backgroundImage = "";

    let hoverBackgroundSelected = 'var(--sign-hover-background)';

    if (IsLeo(theme)) {
        hoverBackgroundSelected = 'var(--sign-hover-background-selected)'
    }

    if (theme === themeC.Hard) {
        hoverBackgroundSelected = 'var(--sign-background)'
    }

    let button: SxProps<Theme> = {
        padding: "0px 5px",
        textAlign: "center",
        height: "40px",
        fontFamily: "var(--font-body)",
        minWidth: "40px",
        maxWidth: "40px",
        marginRight: "10px",
        border: 'var(--sign-border-width) solid var(--sign-border-color, var(--off-black))',
        borderRadius: 'var(--sign-border-radius)',
        background: 'var(--sign-background-not-selected)',
        color: 'var(--sign-color)',
        "&:hover": {
            color: 'var(--sign-hover-color)',
            backgroundColor: isMobileScreen ? 'var(--sign-background-not-selected)' : 'var(--sign-hover-background)',
        },
        "&.Mui-selected": {
            color: 'var(--sign-color-selected)',
            backgroundColor: 'var(--sign-background)',
            borderColor: 'var(--sign-border-color-selected, var(--sign-border-color, var(--off-black)))',
            backgroundImage: backgroundImage,
        },
        "&.Mui-selected:hover": {
            color: 'var(--sign-color-selected)',
            backgroundColor: isMobileScreen ? 'var(--sign-background)' : hoverBackgroundSelected,
            borderColor: 'var(--sign-border-color-selected, var(--sign-border-color, var(--off-black)))',
        }
    }

    let correct: SxProps<Theme> = {
        backgroundColor: 'var(--sign-correct-border)',
        color: 'white'
    }

    if (coupon !== undefined && (theme === themeC.GoGo || theme === themeC.Wood || theme === themeC.Orange || theme === themeC.Lion)) {
        const orangeSelectedBg = IsOrangeLight(theme) ? "#173154" : "#1E88E5";
        const correctBorder = theme === themeC.Wood ? '2px solid #1DD064' : theme === themeC.Lion ? '2px solid #008844' : theme === themeC.Orange ? '2px solid #25852F' : '2px solid #00E68F';
        const correctShadow = theme === themeC.Lion ? 'inset 0 0 0 2px #F6F6F6' : IsOrangeLight(theme) ? 'inset 0 0 0 2px #F5F7FB' : 'inset 0 0 0 2px #1E1E1E';
        correct = {
            border: correctBorder,
            boxShadow: correctShadow,
            "&.Mui-selected": {
                color: (theme === themeC.Orange || theme === themeC.Lion) ? "#FFFFFF" : "#0C0C0C",
                backgroundColor: theme === themeC.Wood ? "#B19661" : theme === themeC.Lion ? "#31609A" : theme === themeC.Orange ? orangeSelectedBg : "#FF34AA",
                border: correctBorder,
                boxShadow: correctShadow,
            },
            "&.Mui-selected:hover": {
                color: (theme === themeC.Orange || theme === themeC.Lion) ? "#FFFFFF" : "#0C0C0C",
                backgroundColor: theme === themeC.Wood ? "#B19661" : theme === themeC.Lion ? "#31609A" : theme === themeC.Orange ? orangeSelectedBg : "#FF34AA",
                border: correctBorder,
                boxShadow: correctShadow,
            },
        }
    } else if (coupon !== undefined) {
        correct = {
            borderColor: 'var(--sign-correct-border)',
            borderWidth: "3px"
        }
    }

    const lastButton: SxProps<Theme> = {
        marginRight: "0px",
    }

    let statButton: SxProps<Theme> = {
        padding: "0px 5px",
        height: "20px",
        minWidth: "20px",
        maxWidth: "20px",
        marginRight: "10px",
        border: '0 solid',
        borderRadius: 'var(--sign-border-radius)',
        background: 'var(--background_2)',
        color: 'var(--button-hover-color)',
        "&:hover": {
            color: 'var(--off-black)',
            backgroundColor: 'var(--background_1)',
        },
        "&.Mui-selected": {
            color: 'var(--off-black)',
            backgroundColor: 'var(--background_1)'
        }
    }

    let signText: SxProps<Theme> = {
        fontSize: "13px",
        lineHeight: "18px",
        fontFamily: "var(--font-body)"
    }

    let teams: SxProps<Theme> = {
        fontSize: "13px",
        lineHeight: "19px",
        fontWeight: "500",
        fontFamily: "var(--font-body)",
        textAlign: isRightToLeft ? "right" : ""
    }

    let time: SxProps<Theme> = {
        fontSize: "12px",
        lineHeight: "19px",
        fontWeight: "400",
        fontFamily: "var(--font-body)",
        marginTop: "2px"
    }

    let gameNr: SxProps<Theme> = {
        width: "20px",
        marginRight: "5px"
    }

    const titleOverview: SxProps<Theme> = {
        fontSize: "13px",
        lineHeight: "19px",
        fontWeight: "bold",
        fontFamily: "var(--font-body)",
        marginTop: "5px",
        marginBottom: "5px",
        color: "var(--text-black-dark-background)"
    }

    const textDetail: SxProps<Theme> = {
        fontSize: "11px",
        lineHeight: "19px",
        fontFamily: "var(--font-body)",
        marginTop: "5px",
        marginBottom: "5px",
        marginLeft: "3px",
        color: "var(--text-black-dark-background)"
    }

    const mobileRowDistStyle: SxProps<Theme> = {
        paddingTop: "20px",
        paddingBottom: "20px",
        marginLeft: isRightToLeft ? "0px" : "20px"
    }

    let goldBox: SxProps<Theme> = {};

    if (IsLeo(theme)) {
        signText.fontWeight = "bold";
    }

    if ((theme === themeC.Kung)) {
        signText = {
            fontSize: "20px",
            fontWeight: 700,
            lineHeight: "23.44px",
            fontFamily: "var(--font-body)"
        }
        button = { ...button, ...{ border: '1px solid #249CEC' } };
        placeBetColor = "linear-gradient(180deg, #66C72A 0%, #238500 100%)";
        statButton = {
            ...statButton, ...{
                color: '#C3E7FF',
                "&:hover": {
                    color: '#249CEC',
                    backgroundColor: 'var(--background_1)',
                },
                "&.Mui-selected": {
                    color: '#249CEC',
                    backgroundColor: 'var(--background_1)'
                }
            }
        };
    }

    if ((theme === themeC.Dbet)) {
        signText = {
            fontSize: "20px",
            fontWeight: 700,
            lineHeight: "23.44px",
            fontFamily: "var(--font-body)"
        }

        teams = {
            fontSize: "14px",
            lineHeight: "16.41px",
            fontWeight: "700",
            fontFamily: "var(--font-body)",
            textAlign: isRightToLeft ? "right" : ""
        }
        time = {
            fontSize: "10px",
            lineHeight: "11.72px",
            fontWeight: "400",
            fontFamily: "var(--font-body)",
            marginTop: "5px"
        }

        gameNr.color = "#5E5E5E";


        button = { ...button, ...{ border: '1px solid #249CEC' } };
        placeBetColor = "linear-gradient(180deg, #4075FF 0%, #4075FF 100%)";
        statButton = {


            ...statButton, ...{
                color: '#C3E7FF',
                "&:hover": {
                    color: '#249CEC',
                    backgroundColor: 'var(--background_1)',
                },
                "&.Mui-selected": {
                    color: '#249CEC',
                    backgroundColor: 'var(--background_1)'
                }
            }
        };
    }

    if ((theme === themeC.Video)) {
        signText = {
            fontSize: "20px",
            fontWeight: 700,
            lineHeight: "23.44px",
            fontFamily: "var(--font-body)"
        }

        teams = {
            fontSize: "14px",
            lineHeight: "16.41px",
            fontWeight: "700",
            fontFamily: "var(--font-body)",
            textAlign: isRightToLeft ? "right" : ""
        }
        time = {
            fontSize: "10px",
            lineHeight: "11.72px",
            fontWeight: "400",
            fontFamily: "var(--font-body)",
            marginTop: "5px"
        }

        gameNr.color = "#5E5E5E";


        button = { ...button, ...{ border: '1px solid #7B6032' } };
        placeBetColor = "var(--button-color)";
        statButton = {


            ...statButton, ...{
                color: '#EEC786',

                "&:hover": {
                    color: '#EEC786',
                    backgroundColor: 'var(--background_1)',
                },
                "&.Mui-selected": {
                    color: '#DD9E37',
                    backgroundColor: 'var(--background_1)'
                }
            }
        };
    }

    if ((theme === themeC.MrVegas)) {
        signText = {
            fontSize: "20px",
            fontWeight: 700,
            lineHeight: "23.44px",
            fontFamily: "var(--font-body)"
        }

        teams = {
            fontSize: "14px",
            lineHeight: "16.41px",
            fontWeight: "700",
            fontFamily: "var(--font-body)",
            textAlign: isRightToLeft ? "right" : ""
        }
        time = {
            fontSize: "10px",
            lineHeight: "11.72px",
            fontWeight: "400",
            fontFamily: "var(--font-body)",
            marginTop: "5px"
        }

        gameNr.color = "#5E5E5E";


        button = { ...button, ...{ border: '1px solid #7B6032' } };
        placeBetColor = "var(--button-color)";
        statButton = {


            ...statButton, ...{
                color: '#EEC786',

                "&:hover": {
                    color: '#EEC786',
                    backgroundColor: 'var(--background_1)',
                },
                "&.Mui-selected": {
                    color: '#DD9E37',
                    backgroundColor: 'var(--background_1)'
                }
            }
        };
    }

    if ((theme === themeC.Mega)) {
        signText = {
            fontSize: "20px",
            fontWeight: 700,
            lineHeight: "23.44px",
            fontFamily: "var(--font-body)"
        }

        teams = {
            fontSize: "14px",
            lineHeight: "16.41px",
            fontWeight: "700",
            fontFamily: "var(--font-body)",
            textAlign: isRightToLeft ? "right" : ""
        }
        time = {
            fontSize: "10px",
            lineHeight: "11.72px",
            fontWeight: "400",
            fontFamily: "var(--font-body)",
            color: "var(--font-color-supportive)",
            marginTop: "5px"
        }

        gameNr.color = "#5E5E5E";


        button = { ...button, ...{ border: '1px solid #7B6032' } };
        placeBetColor = "var(--button-color)";
        statButton = {


            ...statButton, ...{
                color: '#EEC786',

                "&:hover": {
                    color: '#EEC786',
                    backgroundColor: 'var(--background_1)',
                },
                "&.Mui-selected": {
                    color: '#DD9E37',
                    backgroundColor: 'var(--background_1)'
                }
            }
        };
    }

    if ((theme === themeC.Hard)) {

        teams.color = 'var(--text-white)';
        time.color = 'var(--text-white)';
        gameNr.color = 'var(--text-white)';

        placeBetColor = "var(--button-color)";
    }

    if (theme === themeC.GoGo || theme === themeC.Wood) {
        clearBetsBorder = (theme === themeC.GoGo) ? "3px solid #fe8000" : "3px solid #B39761";
        const selectedColor = (theme === themeC.GoGo) ? "#FF34AA" : "#B19661";
        const selectedHover = (theme === themeC.GoGo) ? "#E02090" : "#c8b690";
        const statHoverColor = (theme === themeC.GoGo) ? "#F14AA7" : "#B19661";
        const unselectedBg = (theme === themeC.GoGo) ? "#1E1E1E" : "#252525";
        const selectedBorder = (theme === themeC.GoGo) ? "0.5px solid #B3B3B3" : `0.5px solid ${selectedColor}`;

        teams = {
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: "400",
            fontFamily: "var(--font-body)",
            color: "#F0F0F0",
            textAlign: isRightToLeft ? "right" : ""
        };
        time = {
            fontSize: "12px",
            lineHeight: "16px",
            fontWeight: theme === themeC.Wood ? "500" : "400",
            fontFamily: "var(--font-body)",
            color: "#B3B3B3",
            marginTop: "2px"
        };
        button = {
            padding: "2px 4px",
            textAlign: "center",
            height: "48px",
            minWidth: "48px",
            maxWidth: "48px",
            marginRight: "4px",
            border: "0.5px solid #B3B3B3",
            borderRadius: "4px",
            background: unselectedBg,
            color: "#BDA679",
            fontFamily: "var(--font-body)",
            "&:hover": {
                color: "#BDA679",
                backgroundColor: isMobileScreen ? unselectedBg : "#2A2A2A",
            },
            "&.Mui-selected": {
                color: "#0C0C0C",
                backgroundColor: selectedColor,
                border: selectedBorder,
                backgroundImage: backgroundImage,
            },
            "&.Mui-selected:hover": {
                color: "#0C0C0C",
                backgroundColor: isMobileScreen ? selectedColor : selectedHover,
            }
        };
        signText = {
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: "700",
            fontFamily: "var(--font-body)"
        };
        statButton = {
            ...statButton,
            height: "24px",
            minWidth: "24px",
            maxWidth: "24px",
            marginRight: "6px",
            marginLeft: "6px",
            borderRadius: "4px",
            background: "#1E1E1E",
            color: "#F0F0F0",
            "&:hover": {
                color: statHoverColor,
                backgroundColor: "#2A2A2A",
            },
            "&.Mui-selected": {
                color: "#0C0C0C",
                backgroundColor: "#F0F0F0"
            },
            "&.Mui-selected:hover": {
                color: "#0C0C0C",
                backgroundColor: "#F0F0F0"
            }
        };
    }
    if (theme === themeC.Orange) {
        const isLight = IsOrangeLight(theme);
        clearBetsBorder = "3px solid #FE8000";
        const unselectedBg = isLight ? "#EDF1F7" : "#1B2942";
        const selectedColor = isLight ? "#173154" : "#1E88E5";

        teams = {
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: "400",
            fontFamily: "var(--font-body)",
            color: isLight ? "#1F1F1F" : "#F0F0F0",
            textAlign: isRightToLeft ? "right" : ""
        };
        time = {
            fontSize: "12px",
            lineHeight: "16px",
            fontWeight: "400",
            fontFamily: "var(--font-body)",
            color: isLight ? "#636363" : "#B3B3B3",
            marginTop: "2px"
        };
        button = {
            padding: "2px 4px",
            textAlign: "center",
            height: "48px",
            minWidth: "48px",
            maxWidth: "48px",
            marginRight: "4px",
            border: isLight ? "none" : "0.5px solid #2E4062",
            borderRadius: "4px",
            background: unselectedBg,
            color: isLight ? "#1E5CAF" : "#5B9FE0",
            fontFamily: "var(--font-body)",
            "&:hover": {
                color: isLight ? "#1E5CAF" : "#5B9FE0",
                backgroundColor: isMobileScreen ? unselectedBg : (isLight ? "#E0E6F0" : "#243350"),
            },
            "&.Mui-selected": {
                color: "#FFFFFF",
                backgroundColor: selectedColor,
                border: "0.5px solid " + selectedColor,
                backgroundImage: backgroundImage,
            },
            "&.Mui-selected:hover": {
                color: "#FFFFFF",
                backgroundColor: isMobileScreen ? selectedColor : (isLight ? "#1E3D66" : "#1976D2"),
            }
        };
        signText = {
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: "700",
            fontFamily: "var(--font-body)"
        };
        statButton = {
            ...statButton,
            height: "24px",
            minWidth: "24px",
            maxWidth: "24px",
            marginRight: "6px",
            marginLeft: "6px",
            borderRadius: "4px",
            background: isLight ? "#EDF1F7" : "#1B2942",
            color: isLight ? "#1E5CAF" : "#5B9FE0",
            "&:hover": {
                color: "#FFFFFF",
                backgroundColor: selectedColor,
            },
            "&.Mui-selected": {
                color: "#FFFFFF",
                backgroundColor: selectedColor
            },
            "&.Mui-selected:hover": {
                color: "#FFFFFF",
                backgroundColor: selectedColor
            }
        };
        if (isAfterDeadline || coupon !== undefined) {
            button = {
                ...button,
                background: isLight ? "#CBCBCB" : "#242424",
                color: isLight ? "#A8A8A8" : "#6B6B6B",
                border: isLight ? "none" : "0.5px solid #333333",
                "&:hover": {
                    backgroundColor: isLight ? "#CBCBCB" : "#242424",
                    color: isLight ? "#A8A8A8" : "#6B6B6B",
                },
                "&.Mui-selected": {
                    color: "#FFFFFF",
                    backgroundColor: selectedColor,
                    border: "0.5px solid #FFFFFF",
                },
                "&.Mui-selected:hover": {
                    color: "#FFFFFF",
                    backgroundColor: selectedColor,
                    border: "0.5px solid #FFFFFF",
                }
            };
        }
    }
    if (theme === themeC.Lion) {
        clearBetsBorder = "3px solid #fd5c3c";
        const lionUnselectedBg = "#F6F6F6";
        const lionSelectedBg = "#31609A";

        teams = {
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: "400",
            fontFamily: "'Roboto'",
            color: "#1F1F1F",
            textAlign: isRightToLeft ? "right" : ""
        };
        time = {
            fontSize: "12px",
            lineHeight: "16px",
            fontWeight: "400",
            fontFamily: "'Roboto'",
            color: "#636363",
            marginTop: "2px"
        };
        button = {
            padding: "2px 4px",
            textAlign: "center",
            height: "48px",
            minWidth: "48px",
            maxWidth: "48px",
            marginRight: "4px",
            border: "none",
            borderRadius: "4px",
            background: lionUnselectedBg,
            color: "#2481B3",
            fontFamily: "'Roboto'",
            "&:hover": {
                color: "#2481B3",
                backgroundColor: isMobileScreen ? lionUnselectedBg : "#EBEBEB",
                border: "0.5px solid #31609A",
            },
            "&.Mui-selected": {
                color: "#FFFFFF",
                backgroundColor: lionSelectedBg,
                border: "0.5px solid #173154",
                backgroundImage: backgroundImage,
            },
            "&.Mui-selected:hover": {
                color: "#FFFFFF",
                backgroundColor: isMobileScreen ? lionSelectedBg : "#274F80",
                border: "0.5px solid #173154",
            }
        };
        signText = {
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: "700",
            fontFamily: "'Roboto'"
        };
        statButton = {
            ...statButton,
            height: "24px",
            minWidth: "24px",
            maxWidth: "24px",
            marginRight: "6px",
            marginLeft: "6px",
            borderRadius: "4px",
            background: "#F6F6F6",
            color: "#2481B3",
            "&:hover": {
                color: "#FFFFFF",
                backgroundColor: "#31609A",
            },
            "&.Mui-selected": {
                color: "#FFFFFF",
                backgroundColor: "#31609A"
            },
            "&.Mui-selected:hover": {
                color: "#FFFFFF",
                backgroundColor: "#31609A"
            }
        };
        correct = {
            border: '2px solid #008844',
            boxShadow: 'inset 0 0 0 2px #F6F6F6',
            backgroundColor: '#CBCBCB',
            color: '#A8A8A8',
            "&.Mui-selected": {
                color: "#FFFFFF",
                backgroundColor: lionSelectedBg,
                border: '2px solid #008844',
                boxShadow: 'inset 0 0 0 2px #F6F6F6',
            },
            "&.Mui-selected:hover": {
                color: "#FFFFFF",
                backgroundColor: lionSelectedBg,
                border: '2px solid #008844',
                boxShadow: 'inset 0 0 0 2px #F6F6F6',
            },
        };
        if (isAfterDeadline || coupon !== undefined) {
            button = {
                ...button,
                background: "#CBCBCB",
                color: "#A8A8A8",
                border: "none",
                borderRadius: "4px",
                "&:hover": {
                    backgroundColor: "#CBCBCB",
                    color: "#A8A8A8",
                },
                "&.Mui-selected": {
                    color: "#FFFFFF",
                    backgroundColor: lionSelectedBg,
                    border: "0.5px solid #FFFFFF",
                    borderRadius: "4px",
                },
                "&.Mui-selected:hover": {
                    color: "#FFFFFF",
                    backgroundColor: lionSelectedBg,
                    border: "0.5px solid #FFFFFF",
                    borderRadius: "4px",
                }
            };
        }
    }
    if (theme === themeC.Green) {
        clearBetsBorder = "3px solid #111111";
        statButton = {
            ...statButton,
            height: "24px",
            minWidth: "24px",
            maxWidth: "24px",
        };
    }
    if (theme === themeC.ComeOn) {
        clearBetsBorder = "1px solid #39F56A";
    }
    if (theme === themeC.Hajper) {
        clearBetsBorder = "1px solid #39F589";
    }
    if (theme === themeC.Snabbare) {
        clearBetsBorder = "1px solid #12CE67";
    }
    if (theme === themeC.Reviant) {
        clearBetsBorder = "1px solid #E71F00";
    }
    if (theme === themeC.Lyllo) {
        clearBetsBorder = "1px solid #F02884";
    }
    if (theme === themeC.Stugan) {
        clearBetsBorder = "1px solid #F02884";
    }
    if (theme === themeC.Kryss) {
        clearBetsBorder = "1px solid #6A6C6D";
    }

    if (IsVideo(theme)) {
        clearBetsBorder = "1px solid #474747";
    }

    const displayDrawText = (fixture: Fixture) => {
        const draw = fixture.draw;
        return draw && (draw.status === 'PUBLISHED' || draw.status === 'FINISHED');
    }

    const handlePayClick = () => {
        if (showLogin && !isRealMode) {
            setShowLoginDialog(true);
            operatorAdviceScrollToTop(500);
        }
        else {
            trackBeginCheckout({
                round_id: round.id,
                value: freebet !== undefined ? 0 : Number(couponTotal(round, formData!, stakeMultiplier)),
                currency: round.operatorStakePerRow.currency,
                system_type: formData?.bets[0]?.system.type,
            });
            setShowConfirm(true);
            operatorAdviceScrollToTop(500);
        }
    }

    const handleLoginClick = () => {

        setShowLoginDialog(false);

        let loginEvent = {
            type: "login_user",
            navigationId: round.id,
            navigationKey: "round",
            roundId: round.id
        }
        operatorAdviceLogin(loginEvent);
    }

    const { bets } = formData || {};
    const [bet] = bets || [];
    const { legs } = bet || {};
    const { wholePuts, halfPuts } = getWholeAndHalfPutsFromArray(legs);

    let operatorFeePerRow: PoolXMoney = { amount: 0, currency: round.operatorStakePerRow.currency };
    if (round.operatorFeePerRow !== undefined && round.operatorFeePerRow !== null) {
        operatorFeePerRow = round.operatorFeePerRow;
    }

    if (isMobileScreen) {
        if (showRoundInfo) {
            return (
                <RoundInformation round={round} setShowRoundInfo={setShowRoundInfo} />
            )
        }
        if (showProPick) {
            return (
                <ProPickSelector setProPickRows={updateProPickRows} setShowProPick={(x: boolean) => setShowProPick(x)}
                    fixtures={round.fixtures}
                    operatorStakePerRow={round.operatorStakePerRow ?? { amount: 0 }}
                    operatorFeePerRow={operatorFeePerRow} proPickName={GetProPickLabel(window.theme, t)} />)
        }
        if (showRSystem) {
            return (
                <ReducedSystemSelector reducedSystems={round.reducedSystems}
                    onReset={() => {
                        setChosenRSystem({ type: 'MATHEMATICAL' });
                        setShowRSystem(false);
                    }}
                    onChange={(id: string) => {
                        setChosenRSystem({ type: 'REDUCED', reducedSystemId: id });
                        setShowRSystem(false);
                    }
                    }
                    numGames={round.fixtures.length} chosenRSystem={chosenRSystem} />);
        }
    }

    let reducedWhole = "";
    let reducedHalf = "";
    let systemName = "";

    const showRowDistInline = IsLeo(theme) || IsVideo(theme) || theme === themeC.GoGo || theme === themeC.Wood || theme === themeC.Orange || theme === themeC.Lion;
    const showOddsInline = (IsLeo(theme) || theme === themeC.GoGo || theme === themeC.Wood || theme === themeC.Orange || theme === themeC.Lion) && !isAfterDeadline;
    let showLogin = IsLeo(theme);
    if (IsVideo(theme) && playerResp[0]._links.self.href.indexOf('demo') > -1) {
        showLogin = true;
    }

    let showTooltip = false;

    if (formData.bets[0].system.type === "REDUCED") {
        const parts = formData.bets[0].system.reducedSystemId?.split("_");
        if (parts !== undefined && parts?.length > 2) {
            reducedWhole = '(' + parts[1] + ')';
            reducedHalf = '(' + parts[2] + ')';
            systemName = t('reduced_system') + ' ' + bet.system.reducedSystemId;
        }
        showTooltip = true;
    }

    if (freebet !== undefined) {
        reducedWhole = '(' + freebet.numFull + ')';
        reducedHalf = '(' + freebet.numHalf + ')';
        showTooltip = true;
    }

    function clearTextColor(): string {

        if (theme === themeC.ComeOn) {
            return "#39F56A";
        }

        if (theme === themeC.Hajper) {
            return "#39F589";
        }
        if (theme === themeC.Snabbare) {
            return "#12CE67";
        }
        if (theme === themeC.Reviant) {
            return "#E71F00";
        }
        if (theme === themeC.Lyllo) {
            return "#E70A88";
        }
        if (theme === themeC.Stugan) {
            return "#E70A88";
        }

        return "var(--text-black)";
    }

    let styleOutlinedButton: SxProps<Theme> = {
        fontSize: "14px",
        fontFamily: "var(--font-button)",
        fontWeight: "var(--font-weight-button)",
        color: clearTextColor(),
        textTransform: "var(--text-transform-button)",
        border: clearBetsBorder,
        borderRadius: 'var(--button-border-radius)',
        padding: "12px 20px",
        margin: "10px",
        "&:hover": {
            border: clearBetsBorder
        }

    }

    return (
        <div>

            {showLoginDialog && (
                <LoginDialog open={showLoginDialog} onClose={() => handleLoginClick()} />
            )}

            <form onSubmit={handleSubmit} onError={handleError}>
                {showConfirm && (
                    <ConfirmPayment
                        amount={freebet !== undefined ? 0 : couponTotal(round, formData, stakeMultiplier)}
                        currency={round.operatorStakePerRow.currency}
                        toggle={setShowConfirm}
                        submitting={submitting}
                        submitFailed={submitFailed}
                        submitError={submitError}
                        freebet={freebet}
                        stakePerRow={Number(formData.stakePerRow.amount)}
                        handleCloseDialog={onErrorMsgClose}
                    />
                )}
                <Grid container sx={{ justifyContent: "center", marginBottom: isAfterDeadline ? "0px" : "20px" }}>
                    <Grid container direction="column"
                        sx={{ minWidth: "340px", maxWidth: "500px", background: 'var(--background_2)', margin: "0px" }}>
                        {round.fixtures.map((fixture, index) => {

                            let selected = index === selectedIndex && !isMobileScreen;

                            const basic: SxProps<Theme> = {
                                backgroundColor: selected ? "var(--fixture-selected)" : "",
                                color: selected ? "var(--text-black-dark-background)" : "var(--text-black)",
                                paddingTop: "10px",
                                paddingBottom: "10px",
                                paddingRight: "12px",
                                paddingLeft: "12px",
                                borderBottom: "1px solid var(--off-black)",
                                borderTop: index === 0 ? "1px solid var(--off-black)" : ""
                            };

                            if ((theme === themeC.Kung)) {
                                goldBox = {
                                    borderLeft: selected ? "4px solid #249CEC" : "4px solid #081431",
                                }
                            }

                            if ((theme === themeC.Dbet)) {
                                goldBox = {
                                    borderLeft: selected ? "8px solid #3058B8" : "8px solid #EEEEEE",
                                }
                            }

                            if ((theme === themeC.Video)) {
                                goldBox = {
                                    borderLeft: selected ? "8px solid #DD9E37" : "8px solid #1D1D1D",
                                }
                            }

                            if ((theme === themeC.MrVegas)) {
                                goldBox = {
                                    borderLeft: selected ? "8px solid #18723C" : "8px solid #1D1D1D",
                                }
                            }

                            if (theme === themeC.GoGo || theme === themeC.Wood) {
                                goldBox = {
                                    paddingTop: "8px",
                                    paddingBottom: "0px",
                                    borderBottom: "1px solid #575757",
                                    borderTop: "none"
                                };
                            }

                            if (theme === themeC.Orange) {
                                goldBox = {
                                    paddingTop: "8px",
                                    paddingBottom: "0px",
                                    borderBottom: "1px solid #DDDDDD",
                                    borderTop: "none"
                                };
                            }

                            let styleRow: SxProps<Theme> = { ...basic, ...goldBox };

                            return (
                                <Grid key={"row_header" + index} sx={styleRow}
                                    onClick={() => handleFixtureClick(index)}>
                                    <Grid container key={"row_" + index}
                                        sx={{ flexDirection: "var(--flex-direction)", flexWrap: "nowrap" }}
                                        alignItems="stretch">
                                        <Grid item>
                                            <Typography
                                                sx={{ ...teams, ...gameNr } as SxProps<Theme>}>{index + 1}</Typography>
                                        </Grid>
                                        <Grid item sx={{ flexGrow: 1, paddingRight: "5px" }}>
                                            <Grid container sx={{ flexDirection: "var(--flex-direction)" }}>
                                                <Typography sx={teams}>{getTeamName(fixture.homeTeam, t)}</Typography>
                                            </Grid>
                                            <Grid container sx={{ flexDirection: "var(--flex-direction)" }}>
                                                <Typography sx={teams}>{getTeamName(fixture.awayTeam, t)}</Typography>
                                            </Grid>
                                            <Grid container sx={{ flexDirection: "var(--flex-direction)" }}>
                                                {displayDrawText(fixture) ? <Typography
                                                    sx={{ ...time, ...{ fontWeight: "bold" } }}>{getDrawText(fixture.draw, t, true)}</Typography> :
                                                    <Typography sx={time}>{fixtureTime(fixture, t)}</Typography>}
                                            </Grid>

                                        </Grid>
                                        {isAfterDeadline && new Date(fixture.startingAt) < new Date(Date.now()) && !displayDrawText(fixture) &&
                                            <Grid item sx={{ paddingRight: "40px" }}>
                                                <Grid container sx={{ justifyContent: "center" }}>
                                                    <Typography sx={teams}>{fixture.homeTeam.score}</Typography>
                                                </Grid>
                                                <Grid container sx={{ justifyContent: "center" }}>
                                                    <Typography sx={teams}>{fixture.awayTeam.score}</Typography>
                                                </Grid>
                                            </Grid>
                                        }
                                        {isMobileScreen &&
                                            <Grid item sx={{ minHeight: '100%' }}>
                                                <Grid container alignItems="center" direction="row"
                                                    sx={{ minHeight: '100%' }}>
                                                    <ToggleButton name="stat" value="stat"
                                                        selected={expandedFixtures.indexOf(index) > -1}
                                                        onChange={() => handleExpand(index)} sx={statButton}>
                                                        {theme === themeC.Green ? <UnibetStatsIcon width={24} height={24} /> : <EqualizerIcon />}
                                                    </ToggleButton>
                                                </Grid>
                                            </Grid>
                                        }

                                        <Grid item sx={{ minHeight: '100%' }}>
                                            <Grid container alignItems="center" sx={{
                                                minHeight: '100%',
                                                flexDirection: "var(--flex-direction)",
                                                flexWrap: "nowrap"
                                            }}>
                                                <HtmlTooltip disableHoverListener
                                                    open={open.index === index && open.sign === "ONE" && showTooltip}
                                                    onClose={() => setOpen({ index: -1, sign: "" })}
                                                    disableFocusListener
                                                    disableTouchListener
                                                    placement="top"
                                                    title={TooltipContent(textDetail)}>
                                                    <ToggleButton ref={(el) => (buttonRefs.current[index.toString() + "ONE"] = el)} name="1" onClick={() => handleTooltipToggle(index, "ONE")} onChange={() => handleChange(index, "ONE")}
                                                        selected={isSelected(index, "ONE")}
                                                        value="1" sx={getSignStyle(round, index, "ONE")}>
                                                        <Typography ref={(el) => (typoRefs.current[index.toString() + "ONE"] = el)} sx={signText}>1</Typography>
                                                    </ToggleButton>
                                                </HtmlTooltip>

                                                <HtmlTooltip disableHoverListener
                                                    open={open.index === index && open.sign === "X" && showTooltip}
                                                    onClose={() => setOpen({ index: -1, sign: "" })}
                                                    disableFocusListener
                                                    disableTouchListener
                                                    placement="top"
                                                    title={TooltipContent(textDetail)}>
                                                    <ToggleButton ref={(el) => (buttonRefs.current[index.toString() + "X"] = el)} name="X" onClick={() => handleTooltipToggle(index, "X")} onChange={() => handleChange(index, "X")}
                                                        selected={isSelected(index, "X")}
                                                        value="X" sx={getSignStyle(round, index, "X")}>
                                                        <Typography ref={(el) => (typoRefs.current[index.toString() + "X"] = el)} sx={signText}>X</Typography>
                                                    </ToggleButton>
                                                </HtmlTooltip>

                                                <HtmlTooltip disableHoverListener
                                                    open={open.index === index && open.sign === "TWO" && showTooltip}
                                                    onClose={() => setOpen({ index: -1, sign: "" })}
                                                    disableFocusListener
                                                    disableTouchListener
                                                    placement="top"
                                                    title={TooltipContent(textDetail)}>
                                                    <ToggleButton ref={(el) => (buttonRefs.current[index.toString() + "TWO"] = el)} name="2" onClick={() => handleTooltipToggle(index, "TWO")} onChange={() => handleChange(index, "TWO")}
                                                        selected={isSelected(index, "TWO")}
                                                        value="2" sx={
                                                            isRightToLeft ? getSignStyle(round, index, "TWO") : { ...getSignStyle(round, index, "TWO"), ...lastButton } as SxProps<Theme>}>
                                                        <Typography ref={(el) => (typoRefs.current[index.toString() + "TWO"] = el)} sx={signText}>2</Typography>
                                                    </ToggleButton>
                                                </HtmlTooltip>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    {showRowDistInline &&
                                        <Grid container key={"rowDist_" + index}
                                            sx={(theme === themeC.GoGo || theme === themeC.Wood || theme === themeC.Orange || theme === themeC.Lion) ? { paddingTop: "6px", paddingBottom: "10px" } : {}}>
                                            <Grid container direction="row" sx={{ justifyContent: "space-between", flexDirection: "var(--flex-direction)" }}>
                                                <Typography sx={{ ...time, ...{ marginLeft: "25px", ...((theme === themeC.GoGo || theme === themeC.Wood || IsOrangeDark(theme)) ? { color: "#F0F0F0" } : (theme === themeC.Orange || theme === themeC.Lion) ? { color: "#1F1F1F" } : {}) } }}>{t('row_distribution')}</Typography>

                                                <Grid item>
                                                    {!isRightToLeft &&
                                                        <Grid container direction="row" sx={{ flexDirection: "var(--flex-direction)" }}>
                                                            <Grid container sx={{ justifyContent: "center", marginRight: (theme === themeC.GoGo || theme === themeC.Wood || theme === themeC.Orange || theme === themeC.Lion) ? "4px" : "10px" }} width={(theme === themeC.GoGo || theme === themeC.Wood || theme === themeC.Orange || theme === themeC.Lion) ? "48px" : "40px"}>
                                                                <Typography sx={{ ...time, ...((theme === themeC.GoGo || theme === themeC.Wood || IsOrangeDark(theme)) ? { color: "#F0F0F0" } : (theme === themeC.Orange || theme === themeC.Lion) ? { color: "#1F1F1F" } : {}) }}>{formatFraction(fixture?.signs?.ONE?.rowsFraction ?? 0)}</Typography>
                                                            </Grid>
                                                            <Grid container
                                                                sx={{ justifyContent: "center", marginRight: (theme === themeC.GoGo || theme === themeC.Wood || theme === themeC.Orange || theme === themeC.Lion) ? "4px" : "10px" }}
                                                                width={(theme === themeC.GoGo || theme === themeC.Wood || theme === themeC.Orange || theme === themeC.Lion) ? "48px" : "40px"}>
                                                                <Typography
                                                                    sx={{ ...time, ...((theme === themeC.GoGo || theme === themeC.Wood || IsOrangeDark(theme)) ? { color: "#F0F0F0" } : (theme === themeC.Orange || theme === themeC.Lion) ? { color: "#1F1F1F" } : {}) }}>{formatFraction(fixture?.signs?.X?.rowsFraction ?? 0)}</Typography>
                                                            </Grid>
                                                            <Grid container sx={{ justifyContent: "center" }}
                                                                width={(theme === themeC.GoGo || theme === themeC.Wood || theme === themeC.Orange || theme === themeC.Lion) ? "48px" : "40px"}>
                                                                <Typography
                                                                    sx={{ ...time, ...((theme === themeC.GoGo || theme === themeC.Wood || IsOrangeDark(theme)) ? { color: "#F0F0F0" } : (theme === themeC.Orange || theme === themeC.Lion) ? { color: "#1F1F1F" } : {}) }}>{formatFraction(fixture?.signs?.TWO?.rowsFraction ?? 0)}</Typography>
                                                            </Grid>
                                                        </Grid>
                                                    }
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    }
                                    {showOddsInline && fixture.odds && fixture.odds?.decimalOdds?.ONE > 0 &&
                                        <Grid container key={"rowOdds_" + index}
                                            sx={(theme === themeC.GoGo || theme === themeC.Wood || theme === themeC.Orange || theme === themeC.Lion) ? { paddingTop: "2px", paddingBottom: "6px" } : {}}>
                                            <Grid container direction="row" sx={{ justifyContent: "space-between", flexDirection: "var(--flex-direction)" }}>
                                                <Typography sx={{ ...time, ...{ marginLeft: "25px", color: "var(--game-summary-title)" } }}>{t('odds')}</Typography>

                                                <Grid item>
                                                    {!isRightToLeft &&
                                                        <Grid container direction="row" sx={{ flexDirection: "var(--flex-direction)" }}>
                                                            <Grid container sx={{ justifyContent: "center", marginRight: (theme === themeC.GoGo || theme === themeC.Wood || theme === themeC.Orange || theme === themeC.Lion) ? "4px" : "10px" }} width={(theme === themeC.GoGo || theme === themeC.Wood || theme === themeC.Orange || theme === themeC.Lion) ? "48px" : "40px"}>
                                                                <Typography sx={{ ...time, ...{ color: "var(--game-summary-title)" } }}>{fixture.odds?.decimalOdds?.ONE ?
                                                                    nfOdds.format(fixture.odds?.decimalOdds?.ONE) : ""}</Typography>
                                                            </Grid>
                                                            <Grid container
                                                                sx={{ justifyContent: "center", marginRight: (theme === themeC.GoGo || theme === themeC.Wood || theme === themeC.Orange || theme === themeC.Lion) ? "4px" : "10px" }}
                                                                width={(theme === themeC.GoGo || theme === themeC.Wood || theme === themeC.Orange || theme === themeC.Lion) ? "48px" : "40px"}>
                                                                <Typography
                                                                    sx={{ ...time, ...{ color: "var(--game-summary-title)" } }}>{fixture.odds?.decimalOdds?.X ?
                                                                        nfOdds.format(fixture.odds?.decimalOdds?.X) : ""}</Typography>
                                                            </Grid>
                                                            <Grid container sx={{ justifyContent: "center" }}
                                                                width={(theme === themeC.GoGo || theme === themeC.Wood || theme === themeC.Orange || theme === themeC.Lion) ? "48px" : "40px"}>
                                                                <Typography
                                                                    sx={{ ...time, ...{ color: "var(--game-summary-title)" } }}>{fixture.odds?.decimalOdds?.TWO ?
                                                                        nfOdds.format(fixture.odds?.decimalOdds?.TWO) : ""}</Typography>
                                                            </Grid>
                                                        </Grid>
                                                    }
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    }
                                    {
                                        expandedFixtures.indexOf(index) > -1 &&
                                        <Grid container direction="column">
                                            <Grid item sx={(theme === themeC.GoGo || theme === themeC.Wood || theme === themeC.Orange) ? {} : mobileRowDistStyle}>
                                                {!showRowDistInline &&
                                                    <Grid container direction="row" sx={{ justifyContent: "space-between", flexDirection: "var(--flex-direction)" }}>
                                                        <Typography sx={time}>{t('row_distribution')}</Typography>
                                                        <Grid item>
                                                            {isRightToLeft &&
                                                                <Grid container direction="row" sx={{ flexDirection: "var(--flex-direction)" }}>
                                                                    <Grid container sx={{ justifyContent: "center", marginLeft: "10px" }} width={"40px"}>
                                                                        <Typography sx={time}>1: {formatFraction(fixture?.signs?.ONE?.rowsFraction ?? 0)}</Typography>
                                                                    </Grid>
                                                                    <Grid container
                                                                        sx={{ justifyContent: "center", marginLeft: "10px" }}
                                                                        width={"40px"}>
                                                                        <Typography
                                                                            sx={time}>X: {formatFraction(fixture?.signs?.X?.rowsFraction ?? 0)}</Typography>
                                                                    </Grid>
                                                                    <Grid container sx={{ justifyContent: "center" }}
                                                                        width={"40px"}>
                                                                        <Typography
                                                                            sx={time}>2: {formatFraction(fixture?.signs?.TWO?.rowsFraction ?? 0)}</Typography>
                                                                    </Grid>
                                                                </Grid>
                                                            }
                                                            {!isRightToLeft &&
                                                                <Grid container direction="row" sx={{ flexDirection: "var(--flex-direction)" }}>
                                                                    <Grid container sx={{ justifyContent: "center", marginRight: "10px" }} width={"40px"}>
                                                                        <Typography sx={time}>{formatFraction(fixture?.signs?.ONE?.rowsFraction ?? 0)}</Typography>
                                                                    </Grid>
                                                                    <Grid container
                                                                        sx={{ justifyContent: "center", marginRight: "10px" }}
                                                                        width={"40px"}>
                                                                        <Typography
                                                                            sx={time}>{formatFraction(fixture?.signs?.X?.rowsFraction ?? 0)}</Typography>
                                                                    </Grid>
                                                                    <Grid container sx={{ justifyContent: "center" }}
                                                                        width={"40px"}>
                                                                        <Typography
                                                                            sx={time}>{formatFraction(fixture?.signs?.TWO?.rowsFraction ?? 0)}</Typography>
                                                                    </Grid>
                                                                </Grid>
                                                            }
                                                        </Grid>
                                                    </Grid>
                                                }
                                            </Grid>
                                            <Grid item
                                                sx={(theme === themeC.GoGo || theme === themeC.Orange)
                                                    ? { paddingTop: "8px", paddingBottom: "8px" }
                                                    : showRowDistInline ? { paddingTop: "0px", paddingBottom: "20px", marginLeft: "20px" }
                                                    : { paddingTop: "20px", paddingBottom: "20px", marginLeft: "20px" }}>
                                                {(theme === themeC.GoGo || theme === themeC.Wood) ? (
                                                    <div style={{ border: "1px solid #252525", borderRadius: "12px", overflow: "hidden" }}>
                                                        <SlimFixtureDetail theme={theme} fixture={fixture} isHockey={isHockey} />
                                                    </div>
                                                ) : theme === themeC.Orange ? (
                                                    <div style={{ border: "1px solid #DDDDDD", borderRadius: "8px", overflow: "hidden" }}>
                                                        <SlimFixtureDetail theme={theme} fixture={fixture} isHockey={isHockey} />
                                                    </div>
                                                ) : (
                                                    <SlimFixtureDetail theme={theme} fixture={fixture} isHockey={isHockey} />
                                                )}
                                            </Grid>
                                        </Grid>
                                    }
                                </Grid>
                            );
                        })
                        }

                        {!isAfterDeadline && coupon === undefined &&
                            <Grid container justifyContent="center" direction="column" sx={{
                                padding: "20px",
                                flexDirection: "var(--flex-direction)",
                                backgroundColor: "var(--background_4)"
                            }}>
                                <Typography sx={titleOverview}>{t('overview')}</Typography>
                                <Grid container direction="column" alignItems="center">
                                    <Grid container direction="row" justifyContent="space-between">
                                        <Grid item>
                                            <Grid container
                                                sx={{ alignItems: "center", flexDirection: "var(--flex-direction)" }}>
                                                {CircleIcon}
                                                <Typography
                                                    sx={textDetail}>{t('full_cover')}: {wholePuts} {reducedWhole} </Typography>
                                            </Grid>
                                        </Grid>
                                        <Grid item>
                                            <Grid container
                                                sx={{ alignItems: "center", flexDirection: "var(--flex-direction)" }}>
                                                {HalfCircleIcon}
                                                <Typography
                                                    sx={textDetail}>{t('half_cover')}: {halfPuts} {reducedHalf} </Typography>
                                            </Grid>
                                        </Grid>
                                        {freebet === undefined && <Grid item>
                                            <Grid container
                                                sx={{ alignItems: "center", flexDirection: "var(--flex-direction)" }}>
                                                {CashIcon}
                                                {isRightToLeft ?
                                                    <Typography
                                                        sx={textDetail}>{nf.format(rowPrice)} : {t('row_price')}</Typography> :
                                                    <Typography
                                                        sx={textDetail}>{t('row_price')} : {nf.format(rowPrice)}</Typography>}
                                            </Grid>
                                        </Grid>
                                        }
                                    </Grid>

                                    {freebet !== undefined && !coversValid &&
                                        <Grid container sx={{ maxWidth: "300px", marginTop: "20px" }} direction="row" justifyContent="center" textAlign="center" alignItems="center">
                                            <Typography width="100%" sx={teams}>{t('freebet_wrong_covers')}</Typography>
                                        </Grid>
                                    }

                                    {systemName !== null && systemName !== '' &&
                                        <Grid container direction="row" justifyContent="center">
                                            <Grid item>
                                                <Typography sx={textDetail}>{systemName}</Typography>
                                            </Grid>
                                        </Grid>
                                    }
                                    {freebet === undefined &&
                                        <StakeMultiplier t={t} stakeMultiplier={stakeMultiplier}
                                            setStakeMultiplier={setStakeMultiplier}
                                            isRightToLeft={isRightToLeft} theme={theme}></StakeMultiplier>
                                    }

                                    <Grid item sx={{ topMargin: "20px" }}>
                                        <Grid container direction={isRightToLeft ? "row-reverse" : "row"}
                                            justifyContent={"center"} sx={{ marginTop: "20px" }}>
                                            <MuiButton
                                                sx={styleOutlinedButton}
                                                variant="outlined"
                                                onClick={() => {
                                                    trackCouponTool("clear", { round_id: round.id });
                                                    resetForm();
                                                    setChosenRSystem({ type: 'MATHEMATICAL' });
                                                }}
                                            >
                                                {t('clear_bets')}
                                            </MuiButton>

                                            <Button
                                                disabled={(!isRealMode && !showLogin) || !valid}
                                                onClick={() => handlePayClick()}
                                                type="button"
                                                padding="12px 20px"
                                                margin="10px"
                                                backgroundImage={placeBetColor}
                                            >
                                                {freebet === undefined && t('pay') + ' ' + nf.format(couponTotal(round, formData, stakeMultiplier))}
                                                {freebet !== undefined && t('pay_freebet')}
                                            </Button>

                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        }

                    </Grid>
                </Grid>

            </form>

        </div>
    );

}

export default SlimCoupon;
